import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BRAND, buildWhatsAppUrl } from "@/lib/brand";

// Brand WhatsApp icon
const WhatsAppIcon = ({ size = 26 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.066 0-.115-.014-.157-.028-2.085-1.026-3.96-2.937-4.987-5.026-.014-.038-.025-.082-.025-.122 0-.41 1.387-1.131 1.387-1.508 0-.067-.018-.122-.032-.165-.131-.452-.892-2.357-1.067-2.79-.144-.36-.247-.502-.652-.502-.062 0-.182.018-.205.018-.295.041-.715.131-.999.275-.736.348-1.476 1.426-1.476 2.836 0 1.388.652 2.802 1.62 4.06 1.876 2.36 3.79 4.08 6.595 5.183.853.336 1.524.535 2.041.535 1.146 0 2.563-.745 2.99-1.642.135-.275.275-.81.275-1.32 0-.4-.117-.434-.39-.555-.46-.222-2.045-.985-2.4-1.124z"/>
        <path d="M16.001 0C7.165 0 .003 7.163.003 16c0 2.812.749 5.49 2.094 7.834L0 32l8.36-2.18A15.939 15.939 0 0 0 16.001 32C24.838 32 32 24.837 32 16S24.838 0 16.001 0zm0 28.928c-2.467 0-4.846-.69-6.93-1.995l-.5-.297-5.165 1.347 1.377-4.99-.327-.515A12.83 12.83 0 0 1 3.073 16C3.073 8.875 8.876 3.072 16 3.072c7.125 0 12.927 5.803 12.927 12.928 0 7.124-5.802 12.928-12.926 12.928z"/>
    </svg>
);

export const FloatingWhatsApp = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 320);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Auto-show tooltip once after a delay
    useEffect(() => {
        if (!visible) return;
        const tt = setTimeout(() => setTooltipOpen(true), 1500);
        const hide = setTimeout(() => setTooltipOpen(false), 6500);
        return () => { clearTimeout(tt); clearTimeout(hide); };
    }, [visible]);

    if (!visible) return null;

    const href = buildWhatsAppUrl(
        t("fab.wa_msg", { name: BRAND.name.split(" ")[0] })
    );

    return (
        <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 flex items-end gap-3">
            {tooltipOpen && (
                <button
                    type="button"
                    onClick={() => setTooltipOpen(false)}
                    aria-label={t("fab.close_aria")}
                    className="hidden sm:flex items-center gap-2 bg-white text-stone-900 ring-1 ring-stone-200 shadow-lg rounded-2xl rounded-br-sm pl-4 pr-3 py-2.5 animate-fade-up max-w-[260px] text-left"
                    data-testid="fab-tooltip"
                >
                    <span className="text-sm leading-snug">
                        {t("fab.tooltip_1")}{" "}
                        <span className="text-gold-deep">{t("fab.tooltip_cta")}</span>
                    </span>
                </button>
            )}
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={t("fab.aria")}
                data-testid="fab-whatsapp"
                className="group relative inline-flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-xl shadow-emerald-900/20 transition-all hover:-translate-y-1"
            >
                {/* Subtle pulse ring */}
                <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping pointer-events-none" aria-hidden="true" />
                <WhatsAppIcon size={26} />
            </a>
        </div>
    );
};
